import { delay } from 'redux-saga';
import { takeLatest, call, fork, put, all } from 'redux-saga/effects';
import { show, hide } from 'redux-modal';
import { reset, stopSubmit } from 'redux-form';
import { camelizeKeys, decamelizeKeys } from 'humps';
import { normalize } from 'normalizr';
import API from 'services/GlossaryApi';
import * as actions from 'actions/glossaries';
import * as uiActions from 'actions/glossaryUi';
import * as schemas from 'schemas/glossaries';
import * as actionTypes from 'actionTypes/glossaries';
import * as entityActions from 'actions/entities';
import * as listActions from 'actions/lists';
import * as constants from 'misc/constants';
import * as coreSagas from 'sagas/core';
import { XLSX_TYPE } from 'misc/config';
import { saveFile } from 'misc/utils';
import { setTabTitle } from "actions/globalTabs";
import { setLoading } from 'actions/loading';
import { successNotification } from 'sagas/core';
import { setDownloadProgress } from 'actions/core';


function* list(action) {
  const { params } = action.payload;
  yield put(setLoading(constants.GLOSSARY_LIST, true));
  try {
    const response = yield call(API.list, decamelizeKeys(params));
    const normalized = normalize(camelizeKeys(response.data.results), [schemas.glossarySchema]);
    const totalCount = response.data.count ? response.data.count : 1;
    yield put(entityActions.merge(normalized.entities));
    yield put(listActions.setPaginationResult(
      constants.GLOSSARY_LIST,
      params.page,
      normalized.result,
      totalCount
    ));
    yield put(setLoading(constants.GLOSSARY_LIST, false));
  } catch (error) {
    yield put(actions.listFailure(error));
    yield put(setLoading(constants.GLOSSARY_LIST, false));
  }
}

function* listAll(action) {
  try {
    const response = yield call(API.listAll);
    const normalized = normalize(camelizeKeys(response.data), [schemas.glossarySchema]);
    yield put(entityActions.merge(normalized.entities));
  } catch (error) {
    yield put(actions.listAllFailure(error));
  }
}

// Retrieve ====================================================================
function* retrieve(action) {
  const { id } = action.payload;
  const loadingKey = `${constants.GLOSSARY_DETAIL}/${id}`;
  yield put(setLoading(loadingKey, true));
  try {
    const response = yield call(API.retrieve, id);
    const normalized = normalize(camelizeKeys(response.data), schemas.glossarySchema);
    yield put(entityActions.merge(normalized.entities));
    const columns = Object.entries(response.data["languages"]).map(([key, value]) => [
      key,
      { visible: value, order: 0 },
    ]);
    yield put(uiActions.setLanguageColumns({
      glossaryKey: id,
      columns: Object.fromEntries(columns)
    }));
    yield put(setLoading(loadingKey, false));
  } catch (error) {
    yield put(actions.retrieveFailure(error));
    yield put(setLoading(loadingKey, false));
  }
}

function* upload(action) {
  const { values } = action.payload;
  yield put(setLoading(constants.GLOSSARY_UPLOAD_MODAL, true));
  try {
    const response = yield call(API.upload, values);
    const normalized = normalize(camelizeKeys(response.data.glossary), schemas.glossarySchema);
    yield put(entityActions.merge(normalized.entities));
    yield put(hide(constants.GLOSSARY_UPLOAD_MODAL));
    yield put(reset(constants.GLOSSARY_UPLOAD_FORM));
    yield put(listActions.addPaginationItem(constants.GLOSSARY_LIST, normalized.result))
    yield put(setLoading(constants.GLOSSARY_UPLOAD_MODAL, false));
    yield fork(
      coreSagas.checkTaskProgress,
      response.data.task_id,
      'Glossary registration in progress...',
      'Glossary registration completed'
    );
  } catch (error) {
    yield put(actions.uploadFailure(error));
    if (error.response.status === 400) {
      yield put(stopSubmit(
        constants.GLOSSARY_UPLOAD_FORM,
        { _error: error.response.data.detail }
      ));
    }
    yield put(setLoading(constants.GLOSSARY_UPLOAD_MODAL, false));
  }
}

function* batchDelete(action) {
  const message = `Are you sure to delete selected Glossary?`;
  const confirmed = yield call(coreSagas.confirm, message);
  if (confirmed) {
    const { values } = action.payload;
    yield put(setLoading(constants.CONFIRM_MODAL, true))
    try {
      yield call(API.batchDelete, decamelizeKeys(values));
      yield all(values.items.map(id => put(listActions.removePaginationItem(constants.GLOSSARY_LIST, id))));
      yield fork(coreSagas.successNotification, 'Glossary deleted successfuly.');
      yield put(hide(constants.CONFIRM_MODAL));
      yield put(setLoading(constants.CONFIRM_MODAL, false))
    } catch (error) {
      yield put(actions.batchDeleteFailure(error));
      yield put(setLoading(constants.GLOSSARY_LIST, false));
    }
  }
}


function* exportExcel(action) {
  yield put(show(constants.FILE_DOWNLOAD_PROGRESS_MODAL));
  const { id, name, colKeys } = action.payload;
  try {
    const [downloadPromise, chan] = yield call(
      coreSagas.createDownloader,
      API.exportExcel,
      { id, colKeys }
    );
    yield fork(coreSagas.watchDownloadProgress, chan);
    const response = yield call(() => downloadPromise);
    yield call(saveFile, response.data, name, XLSX_TYPE);
    yield put(actions.exportExcelSuccess());
    yield delay(1000);
    yield put(hide(constants.FILE_DOWNLOAD_PROGRESS_MODAL));
    yield fork(successNotification, `${name} exported successfuly.`);
  } catch (error) {
    yield put(actions.exportExcelFailure(error));
  } finally {
    yield put(setDownloadProgress(0));
  }
}

function* update(action) {
  const { values } = action.payload;
  yield put(setLoading(constants.GLOSSARY_DETAIL_SETTINGS_MODAL, true));
  try {
    const response = yield call(API.update, values.id, decamelizeKeys(values.data));
    const normalized = normalize(camelizeKeys(response.data), schemas.glossarySchema);
    const tabId = constants.GLOSSARY_DETAIL + '/' + values.id;
    yield put(entityActions.merge(normalized.entities));
    yield put(setTabTitle(tabId, 'Glossary Editor: ' + values.data.name));
    yield put(hide(constants.GLOSSARY_DETAIL_SETTINGS_MODAL));
    yield put(reset(constants.GLOSSARY_DETAIL_SETTINGS_FORM));
    yield put(setLoading(constants.GLOSSARY_DETAIL_SETTINGS_MODAL, false));
  } catch (error) {
    yield put(actions.updateFailure(error));
    yield put(setLoading(constants.GLOSSARY_DETAIL_SETTINGS_MODAL, false));
  }
}


function* watchList() {
  yield takeLatest(actionTypes.LIST.REQUEST, list);
}

function* watchListAll() {
  yield takeLatest(actionTypes.LIST_ALL.REQUEST, listAll);
}

function* watchRetrieve() {
  yield takeLatest(actionTypes.RETRIEVE.REQUEST, retrieve);
}

function* watchUpdate() {
  yield takeLatest(actionTypes.UPDATE.REQUEST, update);
}

function* watchBatchDelete() {
  yield takeLatest(actionTypes.BATCH_DELETE.REQUEST, batchDelete);
}

function* watchUpload() {
  yield takeLatest(actionTypes.UPLOAD.REQUEST, upload);
}

function* watchExportExcel() {
  yield takeLatest(actionTypes.EXPORT_EXCEL.REQUEST, exportExcel);
}


export {
  watchList,
  watchListAll,
  watchRetrieve,
  watchUpdate,
  watchBatchDelete,
  watchUpload,
  watchExportExcel
}